import { useState } from 'react';
import { FINGER_LABELS, HOME_KEYS, resolveLayout } from '../../engine/layouts.js';
import { store, useStoreRevision } from '../store';
import { FingerLegend, Keyboard } from '../Keyboard';

const NON_CALIBRATABLE = ['Tab', 'CapsLock', 'ShiftLeft', 'ShiftRight', 'Backspace', 'Enter'];

/** Príručka: domovská pozícia, prsty, pravidlá. */
export function GuideView() {
  useStoreRevision();
  const settings = store.getSettings();
  const layout = resolveLayout(settings.layoutId, store.calibration(settings.layoutId));

  return (
    <>
      <div className="card">
        <h2>Ako sa učiť písať všetkými desiatimi</h2>
        <ul className="small muted" style={{ paddingLeft: 18 }}>
          <li><strong>Domovská pozícia:</strong> ľavá ruka na A S D F, pravá na J K L ; — palce na medzerníku.</li>
          <li><strong>Vždy sa vráť domov.</strong> Po každom stlačení sa prst vráti na svoju domovskú klávesu.</li>
          <li><strong>Shift stláčaj malíčkom opačnej ruky</strong>, než ktorá píše písmeno.</li>
          <li><strong>Nepozeraj na klávesnicu.</strong> Radšej pomaly a naslepo než rýchlo s pohľadom dolu.</li>
          <li><strong>Presnosť pred rýchlosťou.</strong> Rýchlosť príde sama, keď zmiznú chyby.</li>
          <li><strong>Krátko a pravidelne.</strong> Desať minút denne je viac než dve hodiny raz za týždeň.</li>
        </ul>
      </div>

      <div className="card">
        <h2>Ktorý prst na ktorú klávesu</h2>
        <Keyboard layout={layout} nextChar={null} />
        <FingerLegend />
        <table style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Prst</th>
              <th>Domovská klávesa</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(FINGER_LABELS).map(([finger, label]) => {
              const homeCode = {
                'l-pinky': 'KeyA', 'l-ring': 'KeyS', 'l-middle': 'KeyD', 'l-index': 'KeyF',
                'r-index': 'KeyJ', 'r-middle': 'KeyK', 'r-ring': 'KeyL', 'r-pinky': 'Semicolon',
                thumb: 'Space',
              }[finger];
              const pair = homeCode ? layout.labels[homeCode] : null;
              return (
                <tr key={finger}>
                  <td>{label}</td>
                  <td><span className="chip"><span className="k">{pair?.[0] ?? 'medzerník'}</span></span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Domovské klávesy sú vyznačené</h2>
        <p className="muted small" style={{ marginTop: 0 }}>
          Na každej F a J je hmatník. Ak sa stratíš, nájdi ich poslepu — prsty potom sedia samy.
        </p>
        <div className="row">
          {HOME_KEYS.map((code) => (
            <span className="chip" key={code}>
              <span className="k">{layout.labels[code]?.[0] ?? code}</span>
            </span>
          ))}
        </div>
        <p className="small muted" style={{ marginBottom: 0 }}>
          Počas cvičenia sa vždy zvýrazní klávesa pre nasledujúci znak aj prst, ktorým sa má
          stlačiť — vrátane Shiftu, ak ide o veľké písmeno.
        </p>
      </div>
    </>
  );
}

/**
 * Kalibrácia rozloženia.
 *
 * Prečo to existuje: popisy kláves (najmä `§`, `ň`, `ô`, mŕtve klávesy) sa medzi
 * verziami Windows a distribúciami líšia. Namiesto hádania si aplikácia jednoducho
 * nechá používateľa stlačiť klávesy a zapamätá si, čo jeho klávesnica naozaj posiela.
 * Cvičenia a meranie tým nie sú ovplyvnené — opraví sa len vizuálna klávesnica.
 */
export function CalibrateView() {
  useStoreRevision();
  const settings = store.getSettings();
  const calibration = store.calibration(settings.layoutId);
  const layout = resolveLayout(settings.layoutId, calibration);
  const [last, setLast] = useState<string | null>(null);

  const codes = Object.keys(layout.labels).filter((code) => !NON_CALIBRATABLE.includes(code));
  const missing = codes.filter((code) => !(code in calibration));

  const capture = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    if (event.nativeEvent.isComposing || event.key === 'Dead') return;
    if (event.key.length !== 1) return;
    event.preventDefault();
    store.setCalibration(settings.layoutId, { [event.code]: event.key });
    setLast(`${event.code} → ${event.key === ' ' ? 'medzerník' : event.key}`);
  };

  return (
    <>
      <div className="card">
        <h2>Kalibrácia klávesnice</h2>
        <p className="muted small" style={{ marginTop: 0 }}>
          Stlač postupne klávesy a aplikácia si zapamätá, čo tvoja klávesnica naozaj posiela.
          Vďaka tomu sedí vizuálna klávesnica bez ohľadu na verziu Windows alebo rozloženie.
          Cvičenia fungujú správne aj bez kalibrácie — tá opravuje len nápisy na klávesnici.
        </p>
        <p className="small">
          Skalibrované: <strong>{codes.length - missing.length} / {codes.length}</strong>
          {missing.length > 0 && <> · chýba ešte {missing.length}</>}
        </p>

        <div
          tabIndex={0}
          className="text-display"
          style={{ minHeight: 60, fontFamily: 'inherit', fontSize: '1rem', lineHeight: 1.6 }}
          onKeyDown={capture}
          aria-label="Klikni sem a stláčaj klávesy na kalibráciu"
        >
          <strong>Klikni sem a stláčaj klávesy.</strong>
          <div className="muted small">Kalibrujú sa len znaky bez Shiftu. Mŕtve klávesy preskoč.</div>
          {last && <div className="small">Naposledy: <span className="chip"><span className="k">{last}</span></span></div>}
        </div>

        <div className="row" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              store.clearCalibration(settings.layoutId);
              setLast(null);
            }}
          >
            Vymazať kalibráciu
          </button>
          <span className="muted small">Popisy sa vrátia na predvolené hodnoty pre {layout.name}.</span>
        </div>
      </div>

      <div className="card">
        <h2>Aktuálne popisy kláves</h2>
        <Keyboard layout={layout} nextChar={null} uncalibrated={missing} />
        <FingerLegend />
        <p className="muted small">
          Prerušovaný okraj = klávesa, ktorú si ešte nestlačil v kalibrácii (môže mať nesprávny nápis).
        </p>
        <div className="calib-list">
          {codes.map((code) => (
            <span className={`calib-item ${code in calibration ? '' : 'is-empty'}`} key={code}>
              {code.replace('Key', '').replace('Digit', '')}: {layout.labels[code]?.[0] === ' ' ? 'medzerník' : layout.labels[code]?.[0]}
            </span>
          ))}
        </div>
      </div>
    </>
  );
}
